/**
 * @fileoverview Market page — pure metric transforms
 * @module utils/marketAnalytics
 *
 * Everything here is a pure function over plain data (no Firestore, no
 * React). Firestore reads live in services/marketService.js; this module
 * only ever receives already-loaded slates/races/aggregate rows and
 * returns normalized rows or grouped summaries for the UI.
 *
 * Metric definitions match the Market v1 brief exactly:
 *   returnMultiple = totalVirtualEarnings / totalPurchaseCost (null if cost <= 0)
 *   roi            = (totalVirtualEarnings - totalPurchaseCost) / totalPurchaseCost (null if cost <= 0)
 *   win            = finishingPosition === 1
 *   top5           = finishingPosition between 1 and 5 inclusive
 * Settled-only stats (win%, top5%, ROI, return multiple) only ever use
 * rows where status === 'resulted' — upcoming/live runners never leak into
 * historical performance numbers.
 */

import { getRatingBand, getValueBucket, sortByBucketOrder, RATING_BANDS, VALUE_BUCKETS } from './marketBuckets';
import { getShowdownDisplayState } from './showdownStatus';

export const MIN_SETTLED_SAMPLE = 10;

// ─── Small numeric helpers ──────────────────────────────────────────────────

function numOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function average(list) {
  const nums = list.filter(n => typeof n === 'number' && Number.isFinite(n));
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function normaliseKey(key) {
  return String(key || '').trim().toUpperCase();
}

// ─── Metric definitions (section 8) ─────────────────────────────────────────

export function computeOwnershipUserPct(ownershipUserCount, totalEntries) {
  if (!totalEntries || totalEntries <= 0) return null;
  return (ownershipUserCount || 0) / totalEntries;
}

export function computeReturnMultiple(totalVirtualEarnings, totalPurchaseCost) {
  if (!totalPurchaseCost || totalPurchaseCost <= 0) return null;
  return (totalVirtualEarnings || 0) / totalPurchaseCost;
}

export function computeRoi(totalVirtualEarnings, totalPurchaseCost) {
  if (!totalPurchaseCost || totalPurchaseCost <= 0) return null;
  return ((totalVirtualEarnings || 0) - totalPurchaseCost) / totalPurchaseCost;
}

export function isWin(finishingPosition) {
  return finishingPosition === 1;
}

export function isTop5(finishingPosition) {
  return finishingPosition != null && finishingPosition >= 1 && finishingPosition <= 5;
}

export function isScratchedRunner(runner) {
  if (!runner) return false;
  const s = String(runner.status || '').toLowerCase();
  return s === 'scratched' || runner.scratched === true || runner.isScratched === true;
}

/** 'resulted' | 'live' | 'upcoming' — collapses the full race.status lifecycle for Market. */
export function getRunnerRowStatus(race) {
  if (!race) return 'upcoming';
  if (race.status === 'resulted') return 'resulted';
  if (race.status === 'locked') return 'live';
  return 'upcoming';
}

/** Map of normalised runner key -> finishing position (1-based) for a resulted race. */
export function buildPlacementMap(race) {
  const map = new Map();
  if (!race) return map;

  if (Array.isArray(race.results) && race.results.length > 0) {
    race.results.forEach(r => {
      const key = normaliseKey(r.runnerName || r.runnerId);
      const pos = numOrNull(r.position);
      if (key && pos) map.set(key, pos);
    });
    return map;
  }

  const placings = race.result?.officialPlacings;
  if (Array.isArray(placings)) {
    placings.forEach((name, idx) => {
      const key = normaliseKey(name);
      if (key) map.set(key, idx + 1);
    });
  }
  return map;
}

// ─── Runner market row (section 7.A) ────────────────────────────────────────

/**
 * @param {Object} params
 * @param {Array} params.slates - game_slates docs
 * @param {Array} params.races  - race docs, each pre-tagged with `.slateId`
 * @param {Array} params.aggregateRows - from the getMarketAggregates callable
 * @param {Object} params.entriesBySlate - { [slateId]: totalEntriesSubmitted }
 * @returns {Array} normalized runner market rows
 */
export function buildRunnerMarketRows({ slates = [], races = [], aggregateRows = [], entriesBySlate = {} } = {}) {
  const slateById = new Map((slates || []).filter(Boolean).map(s => [s.id, s]));
  const aggByKey = new Map();
  (aggregateRows || []).forEach(a => {
    if (!a) return;
    aggByKey.set(`${a.slateId}::${a.raceId}::${a.runnerKey}`, a);
  });

  const rows = [];

  (races || []).forEach(race => {
    if (!race || !Array.isArray(race.runners) || !race.slateId) return;
    const slate = slateById.get(race.slateId) || null;
    const placementByKey = buildPlacementMap(race);
    const totalEntries = entriesBySlate[race.slateId] ?? slate?.totalEntriesSubmitted ?? 0;
    const status = getRunnerRowStatus(race);
    const isSettled = status === 'resulted';

    race.runners.forEach(runner => {
      if (!runner) return;
      const runnerKey = normaliseKey(runner.id || runner.name || runner.runnerName);
      if (!runnerKey) return;

      const agg = aggByKey.get(`${race.slateId}::${race.id}::${runnerKey}`);
      const ownershipUserCount = agg?.ownershipUserCount || 0;
      const totalEquityPurchased = agg?.totalEquityPurchased || 0;
      const totalPurchaseCost = agg?.totalPurchaseCost || 0;
      const totalVirtualEarnings = agg?.totalVirtualEarnings || 0;
      const finishingPosition = placementByKey.get(runnerKey) ?? null;
      const displayRating = numOrNull(runner.displayRating);
      const valueLabel = getValueBucket(runner.valueBadge);

      rows.push({
        slateId: race.slateId,
        slateName: slate?.gameName || slate?.title || race.slateId,
        raceId: race.id,
        raceNumber: numOrNull(race.raceNumber),
        raceName: race.raceName || '',
        meetingName: race.meetingName || slate?.venueState || '',

        runnerId: runnerKey,
        runnerNumber: numOrNull(runner.runnerNumber),
        runnerName: runner.name || runner.runnerName || runnerKey,
        jockey: runner.jockey || runner.driver || '',
        trainer: runner.trainer || '',

        displayRating,
        ratingBand: getRatingBand(displayRating),
        valueBadge: runner.valueBadge || 'UNKNOWN',
        valueLabel,
        valueIndex: numOrNull(runner.valueIndex),
        sharePrice: numOrNull(runner.sharePrice),
        generatedSharePrice: numOrNull(runner.generatedSharePrice),

        ownershipUserCount,
        ownershipUserPct: computeOwnershipUserPct(ownershipUserCount, totalEntries),
        totalEquityPurchased,
        totalPurchaseCost,

        scratched: isScratchedRunner(runner),
        status,
        isSettled,
        finishingPosition,
        win: isSettled && isWin(finishingPosition),
        top5: isSettled && isTop5(finishingPosition),

        virtualEarnings: totalVirtualEarnings,
        returnMultiple: isSettled ? computeReturnMultiple(totalVirtualEarnings, totalPurchaseCost) : null,
        roi: isSettled ? computeRoi(totalVirtualEarnings, totalPurchaseCost) : null,
      });
    });
  });

  return rows;
}

// ─── Group metric row (section 7.B) — generic grouping helper ──────────────

function buildGroupMetricRows(rows, groupKeyFn, minSample = 0) {
  const groups = new Map();

  (rows || []).forEach(row => {
    if (!row || row.scratched) return;
    const groupKey = groupKeyFn(row);
    if (groupKey == null) return;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        groupKey, label: groupKey,
        runnerCount: 0, settledRunnerCount: 0, wins: 0, top5: 0,
        totalSpend: 0, totalEarnings: 0, ownershipPctSum: 0, ownershipPctCount: 0,
      });
    }
    const g = groups.get(groupKey);
    g.runnerCount += 1;
    if (row.ownershipUserPct != null) {
      g.ownershipPctSum += row.ownershipUserPct;
      g.ownershipPctCount += 1;
    }
    if (row.isSettled) {
      g.settledRunnerCount += 1;
      if (row.win) g.wins += 1;
      if (row.top5) g.top5 += 1;
      if (row.totalPurchaseCost > 0) {
        g.totalSpend += row.totalPurchaseCost;
        g.totalEarnings += row.virtualEarnings || 0;
      }
    }
  });

  return Array.from(groups.values()).map(g => {
    const returnMultiple = computeReturnMultiple(g.totalEarnings, g.totalSpend);
    return {
      groupKey: g.groupKey,
      label: g.label,
      runnerCount: g.runnerCount,
      settledRunnerCount: g.settledRunnerCount,
      wins: g.wins,
      top5: g.top5,
      winPct: g.settledRunnerCount > 0 ? g.wins / g.settledRunnerCount : null,
      top5Pct: g.settledRunnerCount > 0 ? g.top5 / g.settledRunnerCount : null,
      totalSpend: g.totalSpend,
      totalEarnings: g.totalEarnings,
      avgReturn: returnMultiple,
      returnMultiple,
      roi: computeRoi(g.totalEarnings, g.totalSpend),
      avgOwnershipPct: g.ownershipPctCount > 0 ? g.ownershipPctSum / g.ownershipPctCount : null,
      sampleOk: g.settledRunnerCount >= minSample,
    };
  });
}

export function buildRatingPerformance(rows, minSample = 0) {
  return sortByBucketOrder(buildGroupMetricRows(rows, r => r.ratingBand, minSample), RATING_BANDS);
}

export function buildValueBadgePerformance(rows, minSample = 0) {
  return sortByBucketOrder(buildGroupMetricRows(rows, r => r.valueLabel, minSample), VALUE_BUCKETS);
}

// ─── Jockey/trainer row (section 7.C) ───────────────────────────────────────

function buildPersonPerformance(rows, nameFn, minSample) {
  const groups = new Map();

  (rows || []).forEach(row => {
    if (!row || row.scratched) return;
    const name = nameFn(row);
    if (!name) return;
    if (!groups.has(name)) {
      groups.set(name, {
        name, runnerCount: 0, settledRunnerCount: 0, wins: 0, top5: 0,
        ratingSum: 0, ratingCount: 0, totalSpend: 0, totalEarnings: 0,
      });
    }
    const g = groups.get(name);
    g.runnerCount += 1;
    if (row.displayRating != null) {
      g.ratingSum += row.displayRating;
      g.ratingCount += 1;
    }
    if (row.isSettled) {
      g.settledRunnerCount += 1;
      if (row.win) g.wins += 1;
      if (row.top5) g.top5 += 1;
      if (row.totalPurchaseCost > 0) {
        g.totalSpend += row.totalPurchaseCost;
        g.totalEarnings += row.virtualEarnings || 0;
      }
    }
  });

  return Array.from(groups.values())
    .map(g => ({
      name: g.name,
      runnerCount: g.runnerCount,
      settledRunnerCount: g.settledRunnerCount,
      wins: g.wins,
      top5: g.top5,
      winPct: g.settledRunnerCount > 0 ? g.wins / g.settledRunnerCount : null,
      top5Pct: g.settledRunnerCount > 0 ? g.top5 / g.settledRunnerCount : null,
      avgRunnerRating: g.ratingCount > 0 ? g.ratingSum / g.ratingCount : null,
      totalSpend: g.totalSpend,
      totalEarnings: g.totalEarnings,
      roi: computeRoi(g.totalEarnings, g.totalSpend),
      sampleOk: g.settledRunnerCount >= minSample,
    }))
    .sort((a, b) => {
      if (a.sampleOk !== b.sampleOk) return a.sampleOk ? -1 : 1;
      return (b.winPct ?? -1) - (a.winPct ?? -1);
    });
}

export function buildJockeyPerformance(rows, minSample = MIN_SETTLED_SAMPLE) {
  return buildPersonPerformance(rows, r => r.jockey, minSample);
}

export function buildTrainerPerformance(rows, minSample = MIN_SETTLED_SAMPLE) {
  return buildPersonPerformance(rows, r => r.trainer, minSample);
}

// ─── Overview tab (section 10) ──────────────────────────────────────────────

const VALUE_OPPORTUNITY_SCORE = { Bargain: 1, Value: 0.8, Fair: 0.55, Premium: 0.3, Risk: 0.15, Unknown: 0.4 };
const GOOD_VALUE_LABELS = new Set(['Bargain', 'Value', 'Fair']);

function isLiveOrUpcoming(row) {
  return row.status === 'upcoming' || row.status === 'live';
}

function opportunityScore(row) {
  const ratingScore = (row.displayRating ?? 50) / 100;
  const valueScore = VALUE_OPPORTUNITY_SCORE[row.valueLabel] ?? 0.4;
  const ownershipPenalty = row.ownershipUserPct ?? 0.25;
  return ratingScore * 0.5 + valueScore * 0.35 - ownershipPenalty * 0.15;
}

/** Ranked active/upcoming runners worth a look — Overview + Live Market share this. */
export function buildBestOpportunities(rows, { limit = 20 } = {}) {
  return (rows || [])
    .filter(r => !r.scratched && isLiveOrUpcoming(r) && r.sharePrice != null && r.sharePrice > 0)
    .sort((a, b) => opportunityScore(b) - opportunityScore(a))
    .slice(0, limit);
}

export function buildMostPopularRunners(rows, { limit = 20 } = {}) {
  return (rows || [])
    .filter(r => !r.scratched && (r.ownershipUserCount || 0) > 0)
    .sort((a, b) => (b.ownershipUserPct ?? 0) - (a.ownershipUserPct ?? 0) || (b.totalEquityPurchased || 0) - (a.totalEquityPurchased || 0))
    .slice(0, limit);
}

/**
 * Overview KPI grid + headline tables/charts.
 * Metrics that need per-user tracking (not just per-runner aggregates) are
 * intentionally omitted rather than shown wrong — see marketAggregates.js
 * TODO for the future scheduled-aggregation extension that would add them.
 */
export function buildMarketOverview(rows, slates = []) {
  const safeRows = rows || [];
  const settled = safeRows.filter(r => r.isSettled);
  const owned = safeRows.filter(r => (r.ownershipUserCount || 0) > 0);

  const activeShowdowns = (slates || []).filter(s => getShowdownDisplayState(s)?.tabBucket === 'live').length;
  const upcomingShowdowns = (slates || []).filter(s => getShowdownDisplayState(s)?.tabBucket === 'upcoming').length;

  const distinctRunners = new Set(safeRows.map(r => `${r.slateId}::${r.raceId}::${r.runnerId}`)).size;

  const topRatedSettled = settled.filter(r => r.ratingBand === '90+');
  const valueSettled = settled.filter(r => GOOD_VALUE_LABELS.has(r.valueLabel));

  return {
    kpis: {
      activeShowdowns,
      upcomingShowdowns,
      runnersTracked: distinctRunners,
      avgSpendPerRunner: owned.length > 0 ? average(owned.map(r => r.totalPurchaseCost)) : null,
      topRatedWinPct: topRatedSettled.length > 0 ? topRatedSettled.filter(r => r.win).length / topRatedSettled.length : null,
      valueRunnerTop5Pct: valueSettled.length > 0 ? valueSettled.filter(r => r.top5).length / valueSettled.length : null,
      virtualPrizemoneyAnalysed: settled.length > 0 ? settled.reduce((sum, r) => sum + (r.virtualEarnings || 0), 0) : null,
    },
    bestOpportunities: buildBestOpportunities(safeRows),
    mostPopularRunners: buildMostPopularRunners(safeRows),
    ratingPerformance: buildRatingPerformance(safeRows),
    valueBadgePerformance: buildValueBadgePerformance(safeRows),
  };
}

// ─── Live Market tab (section 11) ───────────────────────────────────────────

const LIVE_MARKET_SORTERS = {
  rating: (a, b) => (b.displayRating ?? -1) - (a.displayRating ?? -1),
  value: (a, b) => (VALUE_OPPORTUNITY_SCORE[b.valueLabel] ?? 0) - (VALUE_OPPORTUNITY_SCORE[a.valueLabel] ?? 0),
  underOwned: (a, b) => (a.ownershipUserPct ?? 1) - (b.ownershipUserPct ?? 1) || (b.displayRating ?? -1) - (a.displayRating ?? -1),
  popular: (a, b) => (b.ownershipUserPct ?? -1) - (a.ownershipUserPct ?? -1),
};

export function buildLiveMarketRows(rows, filters = {}) {
  const { slateId = null, ratingBand = null, valueLabel = null, sortBy = 'rating' } = filters;
  let list = (rows || []).filter(r => !r.scratched && isLiveOrUpcoming(r));
  if (slateId) list = list.filter(r => r.slateId === slateId);
  if (ratingBand) list = list.filter(r => r.ratingBand === ratingBand);
  if (valueLabel) list = list.filter(r => r.valueLabel === valueLabel);

  const sorter = LIVE_MARKET_SORTERS[sortBy] || LIVE_MARKET_SORTERS.rating;
  return [...list].sort(sorter);
}

/**
 * "Under-Owned Opportunities" — rating >= 75 (or top band), good value,
 * ownership below the field average, valid share price, not scratched.
 */
export function buildUnderOwnedOpportunities(rows, { limit = 20 } = {}) {
  const eligible = (rows || []).filter(r =>
    !r.scratched && isLiveOrUpcoming(r) && r.sharePrice != null && r.sharePrice > 0 && r.displayRating != null
  );
  if (eligible.length === 0) return [];

  const avgOwnership = average(eligible.map(r => r.ownershipUserPct ?? 0));

  return eligible
    .filter(r => r.displayRating >= 75 && GOOD_VALUE_LABELS.has(r.valueLabel) && (r.ownershipUserPct ?? 0) < avgOwnership)
    .sort((a, b) => (a.ownershipUserPct ?? 0) - (b.ownershipUserPct ?? 0))
    .slice(0, limit);
}

export function buildLiveMarketHighlights(rows) {
  const eligible = (rows || []).filter(r => !r.scratched && isLiveOrUpcoming(r));
  const rated = eligible.filter(r => r.displayRating != null);
  const valued = eligible.filter(r => GOOD_VALUE_LABELS.has(r.valueLabel));
  const owned = eligible.filter(r => (r.ownershipUserCount || 0) > 0);
  const eliteOwned = eligible.filter(r => (r.displayRating ?? 0) >= 80);

  return {
    highestRated: rated.length ? [...rated].sort((a, b) => b.displayRating - a.displayRating)[0] : null,
    bestValue: valued.length
      ? [...valued].sort((a, b) => (VALUE_OPPORTUNITY_SCORE[b.valueLabel] ?? 0) - (VALUE_OPPORTUNITY_SCORE[a.valueLabel] ?? 0) || (b.displayRating ?? 0) - (a.displayRating ?? 0))[0]
      : null,
    mostOwned: owned.length ? [...owned].sort((a, b) => (b.ownershipUserPct ?? 0) - (a.ownershipUserPct ?? 0))[0] : null,
    lowestOwnedElite: eliteOwned.length ? [...eliteOwned].sort((a, b) => (a.ownershipUserPct ?? 1) - (b.ownershipUserPct ?? 1))[0] : null,
  };
}

// ─── Crowd Insights tab (section 13) ────────────────────────────────────────

export function buildMostOwnedWinners(rows, { limit = 20 } = {}) {
  return (rows || [])
    .filter(r => r.isSettled && r.win && (r.ownershipUserPct || 0) > 0)
    .sort((a, b) => (b.ownershipUserPct || 0) - (a.ownershipUserPct || 0))
    .slice(0, limit);
}

/** High ownership, finished outside top 5, flat/negative return. */
export function buildOverOwnedMisses(rows, { limit = 20 } = {}) {
  const eligible = (rows || []).filter(r => r.isSettled && (r.ownershipUserPct || 0) > 0);
  if (eligible.length === 0) return [];

  const avgOwnership = average(eligible.map(r => r.ownershipUserPct || 0));

  return eligible
    .filter(r => (r.ownershipUserPct || 0) >= avgOwnership && !r.top5 && (r.returnMultiple == null || r.returnMultiple < 1))
    .sort((a, b) => (b.ownershipUserPct || 0) - (a.ownershipUserPct || 0))
    .slice(0, limit);
}

/** Low ownership, top-5 finish or strong return, positive return multiple. */
export function buildHiddenGems(rows, { limit = 20 } = {}) {
  const eligible = (rows || []).filter(r => r.isSettled && r.totalPurchaseCost > 0 && r.returnMultiple != null);
  if (eligible.length === 0) return [];

  const avgOwnership = average(eligible.map(r => r.ownershipUserPct || 0));

  return eligible
    .filter(r => (r.ownershipUserPct || 0) < avgOwnership && r.returnMultiple > 0 && (r.top5 || r.returnMultiple > 1))
    .sort((a, b) => (b.returnMultiple || 0) - (a.returnMultiple || 0))
    .slice(0, limit);
}

/**
 * Avg Runners Selected/Spend Per Race — the only participation stats
 * derivable from per-runner aggregates alone. Per-user flags (e.g. "% of
 * users who owned a winner") would need the callable to track per-user
 * booleans during its scan; omitted for v1 rather than guessed at.
 */
export function buildParticipationStats(rows) {
  const raceMap = new Map();
  (rows || []).forEach(r => {
    const key = `${r.slateId}::${r.raceId}`;
    if (!raceMap.has(key)) raceMap.set(key, { selections: 0, spend: 0 });
    const entry = raceMap.get(key);
    entry.selections += r.ownershipUserCount || 0;
    entry.spend += r.totalPurchaseCost || 0;
  });

  const races = Array.from(raceMap.values());
  return {
    avgRunnersSelectedPerRace: races.length > 0 ? average(races.map(r => r.selections)) : null,
    avgSpendPerRace: races.length > 0 ? average(races.map(r => r.spend)) : null,
  };
}

export function buildCrowdInsights(rows) {
  const safeRows = rows || [];
  const scatterEligible = safeRows.filter(r => r.isSettled && r.totalPurchaseCost > 0 && r.ownershipUserPct != null);

  return {
    scatterPoints: scatterEligible.map(r => ({
      runnerName: r.runnerName,
      slateName: r.slateName,
      displayRating: r.displayRating,
      valueLabel: r.valueLabel,
      ownershipUserPct: r.ownershipUserPct,
      returnMultiple: r.returnMultiple,
      virtualEarnings: r.virtualEarnings,
      totalPurchaseCost: r.totalPurchaseCost,
      ownershipUserCount: r.ownershipUserCount,
      finishingPosition: r.finishingPosition,
    })),
    mostOwnedWinners: buildMostOwnedWinners(safeRows),
    overOwnedMisses: buildOverOwnedMisses(safeRows),
    hiddenGems: buildHiddenGems(safeRows),
    participation: buildParticipationStats(safeRows),
  };
}
